import React, { useEffect, useRef, useState } from 'react'
import { RxCross1 } from "react-icons/rx";
import { IoSearchSharp } from "react-icons/io5";
import { FaPlus, FaChevronLeft, FaChevronRight, FaExclamationTriangle, FaRegTrashAlt, FaPlusCircle, FaPencilAlt, FaRegCalendarAlt, FaUser } from "react-icons/fa";
import { toast } from 'react-toastify';
import moment from 'moment/moment';
import v1Instance from '../../Instance/v1Instance';
import Spinner from '../Spinner';
import { useForm } from "react-hook-form";
import { getHours, parseISO } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { setCall } from '../../redux/slices/apiCall';

// iOS Theme (Helper/Theme.swift) — exact values.
const PRIMARY = '#2E5B97';
const PRIMARY_12 = 'rgba(46,91,151,0.12)';
const PRIMARY_10 = 'rgba(46,91,151,0.10)';
const PRIMARY_06 = 'rgba(46,91,151,0.06)';
const GRAY6 = '#F2F2F7';
const TERTIARY_FILL = 'rgba(118,118,128,0.12)';
const SECONDARY = '#5C5C5C';
const TERTIARY = '#C7C7CC';
const BORDER = '#DCDCDC';
const GROUPED_BG = '#F2F2F7';   // systemGroupedBackground
const CARD = '#FFFFFF';         // secondarySystemGroupedBackground (light)

const initials = (n = '') => n.split(' ').map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
const firstName = (n = '') => (n || '').split(' ')[0];
const PRESETS = [1, 5, 10, 15, 20, 30, 40, 50, 60];

const StaffChip = ({ name, selected, onClick }) => (
    <button onClick={onClick} className='flex flex-col items-center shrink-0' style={{ width: 56 }}>
        <div style={{ width: 50, height: 50, borderRadius: '50%', border: selected ? `2px solid ${PRIMARY}` : '2px solid transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className='flex items-center justify-center' style={{ width: 44, height: 44, borderRadius: '50%', background: selected ? PRIMARY : PRIMARY_10, color: selected ? '#fff' : PRIMARY, fontSize: 13, fontWeight: 600 }}>
                {name === 'Any' ? 'Any' : initials(name)}
            </div>
        </div>
        <p className='truncate w-full text-center' style={{ fontSize: 11, fontWeight: 600, marginTop: 4, color: selected ? PRIMARY : SECONDARY }}>{name === 'Any' ? 'Any' : firstName(name)}</p>
    </button>
);

// ── Variation sheet (modal): staff carousel (or locked banner) + variation rows ──
const VariationSheet = ({ category, eligibleStaff, loadingStaff, selectedSpecialist, staffLocked, onPickSpecialist, onAddVariation, onClose }) => {
    const variants = category?.productVariants ?? [];
    return (
        <div className='fixed inset-0 z-[9994] flex items-end sm:items-center justify-center' style={{ background: 'rgba(0,0,0,0.35)' }} onClick={onClose}>
            <div className='bg-white w-full flex flex-col' style={{ maxWidth: 520, maxHeight: '85vh', borderRadius: 14, overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
                <div className='flex items-center justify-between px-4' style={{ height: 50, borderBottom: `1px solid ${BORDER}` }}>
                    <span style={{ width: 48 }} />
                    <p style={{ fontSize: 16, fontWeight: 600 }}>{category?.productName}</p>
                    <button onClick={onClose} style={{ fontSize: 15, fontWeight: 600, color: PRIMARY, width: 48, textAlign: 'right' }}>Done</button>
                </div>

                {staffLocked && selectedSpecialist ? (
                    <div className='flex items-center gap-2 px-5 py-3' style={{ borderBottom: `1px solid ${BORDER}` }}>
                        <FaUser style={{ fontSize: 13, color: PRIMARY }} />
                        <p style={{ fontSize: 13, fontWeight: 600, color: SECONDARY }}>Booking with <span style={{ color: '#1c1c1e' }}>{selectedSpecialist.name}</span></p>
                    </div>
                ) : (
                    <div className='flex gap-3 overflow-x-auto px-5 py-3' style={{ borderBottom: `1px solid ${BORDER}` }}>
                        {loadingStaff ? <div className='py-2'><Spinner /></div> : (
                            <>
                                <StaffChip name='Any' selected={!selectedSpecialist} onClick={() => onPickSpecialist(null)} />
                                {eligibleStaff.map((m) => (
                                    <StaffChip key={m.teamMemberId} name={m.name} selected={selectedSpecialist?.teamMemberId === m.teamMemberId} onClick={() => onPickSpecialist(m)} />
                                ))}
                            </>
                        )}
                    </div>
                )}

                <div className='overflow-y-auto p-4' style={{ background: GROUPED_BG }}>
                    {variants.length === 0 ? (
                        <p className='text-center py-8' style={{ fontSize: 14, fontWeight: 600, color: SECONDARY }}>No options available.</p>
                    ) : variants.map((v) => (
                        <div key={v?.variantId} className='flex items-center justify-between mb-2 px-4 py-3' style={{ background: CARD, borderRadius: 10 }}>
                            <div>
                                <p style={{ fontSize: 14, fontWeight: 600 }}>{v?.name}</p>
                                <p style={{ fontSize: 12, fontWeight: 600, color: SECONDARY }}>{v?.duration || 30} min · {Number(v?.price) ? `$${parseFloat(v.price).toFixed(0)}` : 'Free'}</p>
                            </div>
                            <FaPlusCircle onClick={() => onAddVariation(category, v)} className='cursor-pointer' style={{ fontSize: 22, color: PRIMARY }} />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const CreateAppointment = ({ setModal }) => {
    const nameRef = useRef();
    const [search, setSearch] = useState("");
    const [openSearch, setOpenSearch] = useState(false);
    const [allCustomers, setAllCustomers] = useState([]);
    const [searchLoader, setSearchLoader] = useState(false);
    const [abortController, setAbortController] = useState(new AbortController());

    const [servicesList, setServicesList] = useState([]);
    const [serviceLoader, setServiceLoader] = useState(false);

    const [servicesToShow, setServicesToShow] = useState([]);        // cart
    const [selectedSpecialist, setSelectedSpecialist] = useState(null); // {teamMemberId,name} | null(=Any)
    const [expandedDurationId, setExpandedDurationId] = useState(null);

    // Variation sheet
    const [sheetCategory, setSheetCategory] = useState(null);
    const [sheetStaff, setSheetStaff] = useState([]);
    const [sheetStaffLoading, setSheetStaffLoading] = useState(false);

    // Availability (right panel)
    const [rightVisible, setRightVisible] = useState(false);
    const [availByDate, setAvailByDate] = useState({});
    const [availLoading, setAvailLoading] = useState(false);
    const [monthAnchor, setMonthAnchor] = useState(moment());
    const [selectedDate, setSelectedDate] = useState(null);

    const dispatch = useDispatch();
    const { call } = useSelector((state) => state?.apiCall);

    const { handleSubmit, register, getValues, setValue, watch, formState: { isSubmitting } } = useForm({
        defaultValues: { balance: null, number: null, startAt: null, customerNote: null, customerId: null },
    });
    const formValues = watch();
    const balanceNum = formValues?.balance != null ? parseFloat(String(formValues.balance).split("$")[1]) : null;
    const hasBalanceDue = balanceNum != null && balanceNum < 0;

    const totalDuration = servicesToShow.reduce((a, s) => a + parseInt(s?.durationMinutes || 0), 0);
    const totalAmount = servicesToShow.reduce((a, s) => a + parseInt(s?.amount || 0), 0);
    const canFindAvailability = servicesToShow.length > 0 && !!formValues?.customerId && !hasBalanceDue;
    const variantIdsCsv = () => servicesToShow.map((s) => s?.serviceVariationId).filter(Boolean).join(',');
    const resetAvailability = () => { setRightVisible(false); setAvailByDate({}); setSelectedDate(null); setValue('startAt', null); };

    // ── Customer ──
    const searchCustomers = async () => {
        try {
            setSearchLoader(true);
            abortController.abort();
            const ac = new AbortController(); setAbortController(ac);
            const res = await v1Instance.get(`/customers?search=${search}`, { signal: ac.signal });
            setAllCustomers(res?.data?.data ?? []);
            setSearchLoader(false);
        } catch (err) { if (err?.message !== "canceled") setSearchLoader(false); }
    };
    const handleSelectName = (value) => {
        setValue("balance", ` $${parseFloat(value?.balance).toFixed(2)} `);
        setValue("number", value?.number);
        setValue("customerId", value?.customerId);
        setSearch(value?.name + " " + value?.familyName);
        setOpenSearch(false);
        gettingServices(value?.customerId);
    };
    const clearCustomer = () => {
        setValue("customerId", null); setValue("balance", null); setValue("number", null);
        setSearch(""); setServicesToShow([]); setServicesList([]); setSelectedSpecialist(null); resetAvailability();
    };

    // ── Services ──
    const gettingServices = async (custId) => {
        try {
            setServiceLoader(true);
            const cId = custId ?? getValues('customerId') ?? '';
            const res = await v1Instance.get(`/services?teamMemberId=&customerId=${cId}`);
            setServicesList(res?.data?.data ?? []);
            setServiceLoader(false);
        } catch { setServiceLoader(false); toast.error("Couldn't load services"); }
    };

    const openCategory = async (category) => {
        setSheetCategory(category);
        // staff who can do this category's variants
        try {
            setSheetStaffLoading(true);
            const ids = (category?.productVariants ?? []).map((v) => v?.variantId).filter(Boolean).join(',');
            const res = ids ? await v1Instance.get(`/eligible-staff?variantIds=${ids}`) : { data: { data: [] } };
            setSheetStaff(res?.data?.data ?? []);
        } catch { setSheetStaff([]); } finally { setSheetStaffLoading(false); }
    };

    const addVariation = (category, v) => {
        setServicesToShow((prev) => [...prev, {
            itemId: category?.productId, itemName: category?.productName,
            serviceVariationId: v?.variantId, serviceVariationName: v?.name, serviceVariationVersion: v?.variantVersion,
            durationMinutes: v?.duration || 10, durationOverride: null, amount: v?.price,
            teamMemberId: selectedSpecialist?.teamMemberId ?? null, staff: selectedSpecialist?.name ?? '',
        }]);
        setSheetCategory(null);
        resetAvailability();
    };

    const removeService = (idx) => { setServicesToShow((prev) => prev.filter((_, i) => i !== idx)); resetAvailability(); };
    const setDuration = (idx, mins) => { setServicesToShow((prev) => prev.map((s, i) => i === idx ? { ...s, durationMinutes: mins, durationOverride: mins } : s)); resetAvailability(); };
    const pickSpecialist = (m) => {
        setSelectedSpecialist(m);
        setServicesToShow((prev) => prev.map((s) => ({ ...s, teamMemberId: m?.teamMemberId ?? null, staff: m?.name ?? '' })));
        resetAvailability();
    };

    // ── Availability (behind Find Availability button) ──
    const loadMonth = async (anchor = monthAnchor) => {
        const ids = variantIdsCsv();
        if (!ids || totalDuration <= 0) return;
        setAvailLoading(true);
        try {
            const month = anchor.format('YYYY-MM');
            const tm = selectedSpecialist ? `&teamMemberId=${selectedSpecialist.teamMemberId}` : '';
            const res = await v1Instance.get(`/availability?month=${month}&variantIds=${ids}${tm}&duration=${totalDuration}`);
            const map = {};
            (res?.data?.data?.slots ?? []).forEach((s) => { const d = s.start.slice(0, 10); (map[d] ||= []).push(s); });
            setAvailByDate(map);
        } catch { setAvailByDate({}); } finally { setAvailLoading(false); }
    };
    const findAvailability = async () => { setRightVisible(true); await loadMonth(monthAnchor); };
    const changeMonth = (delta) => { const next = monthAnchor.clone().add(delta, 'month'); setMonthAnchor(next); setAvailByDate({}); loadMonth(next); };

    // ── Book ──
    const genId = (len = 18) => { const c = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'; let r = ''; for (let i = 0; i < len; i++) r += c[Math.floor(Math.random() * c.length)]; return r; };
    const slotsForDay = selectedDate ? (availByDate[selectedDate] || []) : [];
    const staffNameById = (id) => servicesToShow.find((s) => s.teamMemberId === id)?.staff || selectedSpecialist?.name || '';

    const selectSlot = (slot) => {
        setValue('startAt', slot.start);
        if (!selectedSpecialist) { // "Any" → resolve to the slot's staffer
            const tmId = slot.teamMemberIds?.[0];
            setServicesToShow((prev) => prev.map((s) => ({ ...s, teamMemberId: tmId })));
        }
    };

    const handleSave = async (value) => {
        const squareId = genId();
        const resolved = selectedSpecialist?.teamMemberId ?? servicesToShow[0]?.teamMemberId;
        const uniq = [...new Set(servicesToShow.map((s) => s?.teamMemberId))];
        try {
            const products = servicesToShow.map((item) => ({
                appointmentId: squareId, itemId: item?.itemId, itemName: item?.itemName,
                serviceVariationId: item?.serviceVariationId, serviceVariationName: item?.serviceVariationName,
                serviceVariationVersion: item?.serviceVariationVersion, durationMinutes: parseInt(item?.durationMinutes),
                teamMemberId: item?.teamMemberId, amount: parseInt(item?.amount), status: 0,
            }));
            const body = {
                appointmentProducts: products, customerNote: value?.customerNote, startAt: value?.startAt,
                customerId: value?.customerId, status: "ACCEPTED", squareId, sellerNote: "",
                teamMemberId: resolved, version: 1, locationId: "BAF0TQ869SHW9", feedback: 0, isImported: 1, duration: totalDuration,
            };
            const hour = value?.startAt ? getHours(parseISO(value?.startAt)) : null;
            if (!body.customerId) return toast.warning("Customer is required");
            if (servicesToShow.length === 0) return toast.warning("Add at least one service");
            if (!value?.startAt) return toast.warning("Pick a date & time");
            if (hour < 9 || hour > 20) return toast.warning("Appointment must be between 9:00 AM and 8:00 PM");
            if (!resolved) return toast.warning("Select a specialist");
            if (uniq.length > 1) return toast.warning("All services must use the same specialist.");
            if (hasBalanceDue) return toast.warning("Customer has a balance due — settle it before booking.");

            const res = await v1Instance.post("/appointments/book", body);
            const msg = res?.data?.message;
            if (msg === "Appointment already booked") return toast.warning("That time is no longer available");
            if (msg === "Team member is not available") return toast.warning("Specialist is not available then");
            if (msg === "Appointment time must be in minutes of 10") return toast.warning("Time must be in 10-minute increments");
            if (res?.data?.status === "success") { toast.success("Appointment created"); setModal(""); dispatch(setCall(!call)); }
        } catch (error) { toast.error(error?.response?.data?.error?.message || "Please try again later"); }
    };

    // ── Effects ──
    useEffect(() => {
        const onClick = (e) => { if (nameRef.current && !nameRef.current.contains(e.target)) setOpenSearch(false); };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);
    useEffect(() => { if (search?.length > 0) searchCustomers(); else setOpenSearch(false); }, [search]);

    const monthGrid = () => {
        const start = monthAnchor.clone().startOf('month').startOf('week');
        return Array.from({ length: 42 }, (_, i) => start.clone().add(i, 'days'));
    };
    const QUICK = [['Today', 0], ['1 Week', 7], ['2 Weeks', 14], ['3 Weeks', 21], ['4 Weeks', 28], ['5 Weeks', 35], ['6 Weeks', 42]];
    const selectedStaffName = servicesToShow.find((s) => s.teamMemberId)?.staff || selectedSpecialist?.name;

    return (
        <>
            {sheetCategory && (
                <VariationSheet
                    category={sheetCategory} eligibleStaff={sheetStaff} loadingStaff={sheetStaffLoading}
                    selectedSpecialist={selectedSpecialist} staffLocked={servicesToShow.length > 0 && !!selectedSpecialist}
                    onPickSpecialist={pickSpecialist} onAddVariation={addVariation} onClose={() => setSheetCategory(null)}
                />
            )}

            <div className='ios-cal fixed inset-0 z-[9992] flex justify-center items-center' style={{ background: 'rgba(74,74,74,0.63)' }}>
                <div className='rounded-xl overflow-hidden flex flex-col bg-white' style={{ width: 960, maxWidth: '95vw', height: 680, maxHeight: '92vh' }}>
                    <div className='flex items-center justify-between px-5' style={{ height: 52, borderBottom: `1px solid ${BORDER}` }}>
                        <p style={{ fontSize: 16, fontWeight: 600 }}>New Appointment</p>
                        <RxCross1 onClick={() => setModal("")} className='cursor-pointer' style={{ fontSize: 18, color: SECONDARY }} />
                    </div>

                    <div className='flex flex-1 min-h-0'>
                        {/* LEFT */}
                        <div className='flex flex-col min-h-0' style={{ width: 400, borderRight: `1px solid ${BORDER}`, background: GROUPED_BG }}>
                            {/* Customer */}
                            <p style={{ fontSize: 13, fontWeight: 600, color: SECONDARY, textTransform: 'uppercase', letterSpacing: 0.5, padding: '20px 20px 10px' }}>Customer</p>
                            <div className='px-5 pb-5'>
                                {formValues?.customerId ? (
                                    <>
                                        <div className='flex items-center gap-3 p-3' style={{ background: CARD, borderRadius: 10 }}>
                                            <div className='flex items-center justify-center' style={{ width: 36, height: 36, borderRadius: '50%', background: PRIMARY_12, color: PRIMARY, fontSize: 14, fontWeight: 600 }}>{(search || '?').charAt(0).toUpperCase()}</div>
                                            <div className='flex-1'>
                                                <p style={{ fontSize: 15, fontWeight: 600 }}>{search}</p>
                                                {formValues?.number && <p style={{ fontSize: 12, fontWeight: 600, color: SECONDARY }}>{formValues.number}</p>}
                                            </div>
                                            <RxCross1 onClick={clearCustomer} className='cursor-pointer' style={{ fontSize: 16, color: TERTIARY }} />
                                        </div>
                                        {hasBalanceDue && (
                                            <div className='flex items-center gap-2 mt-2 px-3 py-2' style={{ background: 'rgba(255,0,0,0.07)', border: '1px solid rgba(255,0,0,0.25)', borderRadius: 8 }}>
                                                <FaExclamationTriangle style={{ fontSize: 13, color: '#FF0000' }} />
                                                <p style={{ fontSize: 13, fontWeight: 600, color: '#FF0000' }}>Balance due of ${Math.abs(balanceNum).toFixed(2)}. Settle before booking.</p>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className='relative' ref={nameRef}>
                                        <div className='flex items-center gap-2 p-3' style={{ background: CARD, borderRadius: 10 }}>
                                            <IoSearchSharp style={{ color: SECONDARY }} />
                                            <input value={search} onChange={(e) => { setSearch(e.target.value); setOpenSearch(true); }} placeholder='Search by name or phone' className='w-full bg-transparent outline-none' style={{ fontSize: 14, fontWeight: 600 }} />
                                        </div>
                                        {openSearch && search?.length > 0 && (
                                            <div className='absolute left-0 right-0 z-30 mt-1 overflow-y-auto' style={{ maxHeight: 240, background: CARD, borderRadius: 10, boxShadow: '0 6px 20px rgba(0,0,0,0.12)' }}>
                                                {searchLoader ? <div className='py-4'><Spinner /></div> : allCustomers?.length ? allCustomers.map((c) => (
                                                    <div key={c.customerId} onClick={() => handleSelectName(c)} className='flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50'>
                                                        <div className='flex items-center justify-center' style={{ width: 32, height: 32, borderRadius: '50%', background: PRIMARY_10, color: PRIMARY, fontSize: 13, fontWeight: 600 }}>{(c?.name || '?').charAt(0).toUpperCase()}</div>
                                                        <div>
                                                            <p style={{ fontSize: 14, fontWeight: 600 }}>{c?.name} {c?.familyName} {c?.membership === 2 ? '· Plus Member' : c?.membership === 1 ? '· Plus Basic' : ''}</p>
                                                            {c?.number && <p style={{ fontSize: 12, fontWeight: 600, color: SECONDARY }}>{c.number}</p>}
                                                        </div>
                                                    </div>
                                                )) : <p className='text-center py-3' style={{ fontSize: 13, fontWeight: 600, color: SECONDARY }}>No customers found</p>}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div style={{ borderTop: `1px solid ${BORDER}` }} />
                            <p style={{ fontSize: 13, fontWeight: 600, color: SECONDARY, textTransform: 'uppercase', letterSpacing: 0.5, padding: '20px 20px 10px' }}>Services</p>

                            {/* Services accordion (scroll) */}
                            <div className='flex-1 overflow-y-auto px-4 pb-5'>
                                {serviceLoader ? <div className='py-5'><Spinner /></div> : !formValues?.customerId ? (
                                    <p style={{ fontSize: 13, fontWeight: 600, color: SECONDARY }} className='px-1'>Select a customer first.</p>
                                ) : servicesList.map((cat) => (
                                    <button key={cat?.productId} onClick={() => openCategory(cat)} className='flex items-center justify-between w-full mb-1.5 px-3.5 py-3' style={{ background: CARD, borderRadius: 10 }}>
                                        <p style={{ fontSize: 14, fontWeight: 600 }}>{cat?.productName}</p>
                                        <FaChevronRight style={{ fontSize: 11, color: SECONDARY }} />
                                    </button>
                                ))}
                            </div>

                            {/* Pinned cart */}
                            {servicesToShow.length > 0 && (
                                <div style={{ background: CARD, borderTop: `1px solid ${BORDER}` }}>
                                    {servicesToShow.map((svc, idx) => {
                                        const expanded = expandedDurationId === svc.serviceVariationId;
                                        const presets = PRESETS.includes(Number(svc.durationMinutes)) ? PRESETS : [...PRESETS, Number(svc.durationMinutes)].sort((a, b) => a - b);
                                        return (
                                            <div key={idx} style={{ borderBottom: `1px solid ${BORDER}` }}>
                                                <div className='flex items-start gap-2.5 px-4 py-2.5'>
                                                    <div className='flex-1'>
                                                        <p style={{ fontSize: 13, fontWeight: 600 }}>{svc?.itemName}</p>
                                                        <button onClick={() => setExpandedDurationId(expanded ? null : svc.serviceVariationId)} className='flex items-center gap-1'>
                                                            <span style={{ fontSize: 11, fontWeight: 600, color: svc.durationOverride ? PRIMARY : SECONDARY }}>{svc?.serviceVariationName !== svc?.itemName ? svc?.serviceVariationName + ' · ' : ''}{svc?.durationMinutes} min</span>
                                                            <FaPencilAlt style={{ fontSize: 9, color: 'rgba(46,91,151,0.7)' }} />
                                                        </button>
                                                    </div>
                                                    <p style={{ fontSize: 13, fontWeight: 600, color: PRIMARY }}>{Number(svc?.amount) ? `$${parseFloat(svc.amount).toFixed(0)}` : 'Free'}</p>
                                                    <FaRegTrashAlt onClick={() => removeService(idx)} className='cursor-pointer' style={{ fontSize: 12, color: 'rgba(255,0,0,0.7)' }} />
                                                </div>
                                                {expanded && (
                                                    <div className='flex flex-wrap gap-1.5 px-4 pb-2'>
                                                        {presets.map((m) => {
                                                            const sel = m === Number(svc.durationMinutes);
                                                            return <button key={m} onClick={() => setDuration(idx, m)} style={{ fontSize: 11, fontWeight: 600, padding: '4px 9px', borderRadius: 5, background: sel ? PRIMARY : TERTIARY_FILL, color: sel ? '#fff' : PRIMARY }}>{m}m</button>;
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                    <div className='flex items-center gap-2 px-4 py-2.5'>
                                        <p style={{ fontSize: 14, fontWeight: 600 }}>Total</p>
                                        <div className='flex-1' />
                                        <p style={{ fontSize: 13, fontWeight: 600, color: SECONDARY }}>{totalDuration} min</p>
                                        <p style={{ fontSize: 13, fontWeight: 600, color: TERTIARY }}>·</p>
                                        <p style={{ fontSize: 14, fontWeight: 600, color: PRIMARY }}>{totalAmount === 0 ? 'Free' : `$${totalAmount.toFixed(0)}`}</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* RIGHT */}
                        <div className='flex-1 min-h-0 flex flex-col' style={{ background: GROUPED_BG }}>
                            {!rightVisible ? (
                                <div className='flex flex-col items-center justify-center h-full text-center px-8 gap-5'>
                                    <FaRegCalendarAlt style={{ fontSize: 40, color: 'rgba(46,91,151,0.5)' }} />
                                    <div>
                                        <p style={{ fontSize: 17, fontWeight: 600 }}>Find Availability</p>
                                        <p style={{ fontSize: 14, fontWeight: 600, color: SECONDARY, marginTop: 6 }}>{canFindAvailability ? 'See open time slots' : 'Select a customer and at least one service'}</p>
                                    </div>
                                    <button disabled={!canFindAvailability || availLoading} onClick={findAvailability} className='disabled:opacity-40 flex items-center justify-center' style={{ width: 200, height: 44, borderRadius: 10, background: PRIMARY, color: '#fff', fontSize: 15, fontWeight: 600 }}>{availLoading ? <Spinner /> : 'Find Availability'}</button>
                                </div>
                            ) : (
                                <div className='flex-1 overflow-y-auto p-5'>
                                    <div className='p-4' style={{ background: CARD, borderRadius: 12 }}>
                                        {/* Specialist banner */}
                                        {selectedSpecialist && (
                                            <div className='flex items-center gap-2 px-3 py-2.5 mb-3.5' style={{ background: PRIMARY_06, borderRadius: 10 }}>
                                                <FaUser style={{ fontSize: 13, color: PRIMARY }} />
                                                <p style={{ fontSize: 13, fontWeight: 600 }}>{selectedSpecialist.name}</p>
                                                <div className='flex-1' />
                                                <button onClick={() => pickSpecialist(null)} style={{ fontSize: 12, fontWeight: 600, color: PRIMARY, background: PRIMARY_10, padding: '5px 10px', borderRadius: 999 }}>Change</button>
                                            </div>
                                        )}
                                        {/* Quick picks */}
                                        <div className='flex gap-2 overflow-x-auto mb-3.5'>
                                            {QUICK.map(([label, days]) => {
                                                const d = moment().add(days, 'days'); const key = d.format('YYYY-MM-DD');
                                                const sel = selectedDate === key; const has = (availByDate[key] || []).length > 0;
                                                return <button key={label} onClick={() => { if (!d.isSame(monthAnchor, 'month')) { setMonthAnchor(d); loadMonth(d); } setSelectedDate(key); setValue('startAt', null); }} className='shrink-0' style={{ fontSize: 12, fontWeight: 600, padding: '6px 10px', borderRadius: 999, background: sel ? PRIMARY : has ? PRIMARY_12 : GRAY6, color: sel ? '#fff' : PRIMARY }}>{label}</button>;
                                            })}
                                        </div>
                                        {/* Month header */}
                                        <div className='flex items-center justify-between mb-2'>
                                            <FaChevronLeft onClick={() => changeMonth(-1)} className='cursor-pointer p-2' style={{ fontSize: 15, color: PRIMARY }} />
                                            <div className='flex items-center gap-2'>
                                                <p style={{ fontSize: 16, fontWeight: 600, color: PRIMARY }}>{monthAnchor.format('MMMM YYYY')}</p>
                                                {availLoading && <Spinner />}
                                            </div>
                                            <FaChevronRight onClick={() => changeMonth(1)} className='cursor-pointer p-2' style={{ fontSize: 15, color: PRIMARY }} />
                                        </div>
                                        {/* Day names */}
                                        <div className='grid grid-cols-7 mb-1'>
                                            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => <p key={d} className='text-center' style={{ fontSize: 11, fontWeight: 600, color: 'rgba(46,91,151,0.6)' }}>{d}</p>)}
                                        </div>
                                        {/* Day cells */}
                                        <div className='grid grid-cols-7 gap-1.5'>
                                            {monthGrid().map((m) => {
                                                const inMonth = m.month() === monthAnchor.month();
                                                const key = m.format('YYYY-MM-DD');
                                                const has = (availByDate[key] || []).length > 0;
                                                const past = m.isBefore(moment(), 'day');
                                                const sel = selectedDate === key;
                                                const isToday = m.isSame(moment(), 'day');
                                                return (
                                                    <div key={key} className='relative flex items-center justify-center'>
                                                        <button disabled={!has || past} onClick={() => { setSelectedDate(key); setValue('startAt', null); }} className='flex items-center justify-center' style={{ width: '100%', minHeight: 36, borderRadius: 999, background: sel ? PRIMARY : (has && !past) ? PRIMARY_12 : 'transparent', color: sel ? '#fff' : (past || !has) ? 'rgba(92,92,92,0.4)' : '#1c1c1e', fontSize: 14, fontWeight: 300, cursor: (has && !past) ? 'pointer' : 'default' }}>
                                                            {inMonth ? m.date() : ''}
                                                        </button>
                                                        {isToday && !sel && <span className='absolute' style={{ bottom: 2, width: 4, height: 4, borderRadius: 999, background: PRIMARY }} />}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Time slots */}
                                    {selectedDate && (
                                        <div className='mt-6'>
                                            <p style={{ fontSize: 17, fontWeight: 600, color: PRIMARY, marginBottom: 12 }}>Select a Time</p>
                                            {slotsForDay.length === 0 ? <p style={{ fontSize: 15, fontWeight: 500, color: SECONDARY }}>No available times on this date</p> : (
                                                <div className='grid grid-cols-3 gap-2.5'>
                                                    {slotsForDay.map((slot) => {
                                                        const sel = formValues?.startAt === slot.start;
                                                        return <button key={slot.start} onClick={() => selectSlot(slot)} style={{ padding: '10px 0', borderRadius: 6, background: sel ? PRIMARY : GRAY6, color: sel ? '#fff' : PRIMARY, fontSize: 13 }}>{moment(slot.start).format('h:mm A')}</button>;
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Book */}
                                    {formValues?.startAt && (
                                        <div className='mt-6'>
                                            <div style={{ borderTop: `1px solid ${BORDER}` }} className='pt-3.5'>
                                                <div className='flex items-center justify-between p-3.5' style={{ background: CARD, borderRadius: 10 }}>
                                                    <div>
                                                        <p style={{ fontSize: 15, fontWeight: 600 }}>{moment(formValues.startAt).format('ddd, MMM D · h:mm A')}</p>
                                                        <p style={{ fontSize: 13, fontWeight: 600, color: SECONDARY }}>{totalDuration} min{selectedStaffName ? ` · ${selectedStaffName}` : ''}</p>
                                                    </div>
                                                    <p style={{ fontSize: 22, fontWeight: 600, color: PRIMARY }}>{totalAmount === 0 ? 'Free' : `$${totalAmount.toFixed(0)}`}</p>
                                                </div>
                                                <input {...register("customerNote")} placeholder='Customer note (optional)' className='w-full mt-3 p-3 outline-none' style={{ background: CARD, borderRadius: 10, fontSize: 14, fontWeight: 600 }} />
                                                <button disabled={isSubmitting || hasBalanceDue} onClick={handleSubmit(handleSave)} className='w-full mt-3 flex items-center justify-center disabled:opacity-40' style={{ minHeight: 48, borderRadius: 10, background: PRIMARY, color: '#fff', fontSize: 16, fontWeight: 600 }}>{isSubmitting ? <Spinner /> : 'Book Appointment'}</button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CreateAppointment;
